import { useState, type ReactNode } from "react";
import { Navigate, Outlet, useMatch } from "react-router-dom";
import { cn } from "@dearbill/shared";
import { usePartnerSession } from "../auth/usePartnerSession";
import { PartnerMeProvider } from "../auth/PartnerMeProvider";
import layout from "./WorkspaceLayout.module.css";
import { TopBar } from "./TopBar";

export interface AuthenticatedLayoutContext {
  tourSlot: HTMLDivElement | null;
}

/**
 * Layout route for every authenticated page: the org list, `/profile` and all
 * of `/orgs/:orgId/*`. Without a session it redirects to `/login`. With one it
 * provides the shared `/me` record (`PartnerMeProvider`) and renders the global
 * `TopBar` above the routed content.
 *
 * The unauthenticated routes live outside this layout and never show the top bar.
 */
export function AuthenticatedLayout(): ReactNode {
  const { session } = usePartnerSession();
  const [tourSlot, setTourSlot] = useState<HTMLDivElement | null>(null);
  const onIntroduction = useMatch("/orgs/:orgId/get-started") !== null;
  if (!session) return <Navigate to="/login" replace />;

  return (
    <PartnerMeProvider>
      <div
        className={cn(
          "bg-background flex min-h-screen flex-col",
          !onIntroduction && layout.fixedFrame,
        )}
      >
        <TopBar tourSlotRef={setTourSlot} />
        <div className={cn("flex min-w-0 flex-1 flex-col", !onIntroduction && layout.viewport)}>
          <Outlet context={{ tourSlot } satisfies AuthenticatedLayoutContext} />
        </div>
      </div>
    </PartnerMeProvider>
  );
}
